import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Toastify } from "../../../components/Toaster";
import { RTKSideEffectSchema } from "../index";
import { V2ErrorResponseSchema } from "../../actions/index";
import { getOutcomeTrackingState, getRequestTrackingState, MutationTrackingState } from "../../../services/analytics/tracking";

interface ProfilingStep {
  Step: string;
  Scopes: any;
  Schema: {
    Checked: boolean;
    type: string;
    name: string;
    display: string;
    rules: string;
    options: any;
    permission: any;
    DataSource: any;
    Parent: string;
    ParentDataSource: any;
  }[];
}

export interface ProgressiveProfilingDataState extends RTKSideEffectSchema {
  Data: ProfilingStep[];
}

const initialGetProfilingState: ProgressiveProfilingDataState = {
  Data: []
};

interface GetProgressiveProfilingDataAPIResponse extends V2ErrorResponseSchema {
  Data: ProfilingStep[];
}

interface UpdateProgressiveProfilingSuccessPayload extends ProfilingStep {
  requestedStep?: string;
}

export interface UpdateProgressiveProfilingPayload {
  Step: string;
  Scopes: any;
  Schema: {
    Display: string;
    Enabled: boolean;
    IsMandatory: boolean;
    Parent: string;
    ParentDataSource: any;
    name: string;
    options: any;
    rules: string;
    status: string;
    type: string;
    Checked: boolean;
    permission: any;
    DataSource: any;
  }[];
}

export interface DeleteProgressiveProfilingPayload {
  step: string;
}

export const ProgressiveProfilingDataSlice = createSlice({
  name: "progressiveProfilingData",
  initialState: initialGetProfilingState,
  reducers: {
    getProgressiveProfilingDataRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.isDeleting = false;
      state.isUpdating = false;
      state.error = "";
    },
    getProgressiveProfilingDataSuccess(state, action: PayloadAction<GetProgressiveProfilingDataAPIResponse>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.Data = action.payload.Data;
    },
    getProgressiveProfilingDataFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    updateProgressiveProfilingDataRequest(state, action: PayloadAction<UpdateProgressiveProfilingPayload>) {
      state.isUpdating = true;
      state.isSuccess = false;
      state.isError = false;
      state.error = "";
      Object.assign(state, getRequestTrackingState(action));
    },
    updateProgressiveProfilingDataSuccess(state, action: PayloadAction<UpdateProgressiveProfilingSuccessPayload>) {
      const previousSteps = state.Data ? [...state.Data] : [];
      const { requestedStep, Step, Schema, Scopes } = action.payload;
      const isExistingStep = requestedStep ? previousSteps.some((item) => item.Step === requestedStep) : false;
      const updatedStep: ProfilingStep = { Step, Schema, Scopes };

      const updatedSteps = isExistingStep ? previousSteps.map((item) => (item.Step === requestedStep ? updatedStep : item)) : [...previousSteps, updatedStep];

      state.isUpdating = false;
      state.isSuccess = true;
      state.isError = false;
      state.error = "";
      state.Data = updatedSteps;
      Object.assign(state, getOutcomeTrackingState(state as MutationTrackingState, "success"));

      if (requestedStep) {
        Toastify("success", isExistingStep ? "Step updated successfully" : "Step added successfully");
      } else {
        Toastify("success", "Step saved successfully");
      }
    },
    updateProgressiveProfilingDataFailure(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.isError = true;
      state.error = action.payload;
      Object.assign(state, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      Toastify("error", action.payload);
    },
    deleteProgressiveProfilingDataRequest(state, action: PayloadAction<DeleteProgressiveProfilingPayload>) {
      state.isDeleting = true;
      state.isSuccess = false;
      state.isError = false;
      state.error = "";
      Object.assign(state, getRequestTrackingState(action));
    },
    deleteProgressiveProfilingDataSuccess(state, action: PayloadAction<DeleteProgressiveProfilingPayload>) {
      state.isDeleting = false;
      state.isSuccess = true;
      state.Data = state.Data.filter((item) => item.Step !== action.payload.step);
      Object.assign(state, getOutcomeTrackingState(state as MutationTrackingState, "success"));
      Toastify("success", "Progressive Profiling Step Deleted Successfully");
    },
    deleteProgressiveProfilingDataFailure(state, action: PayloadAction<string>) {
      state.isDeleting = false;
      state.isError = true;
      state.error = action.payload;
      Object.assign(state, getOutcomeTrackingState(state as MutationTrackingState, "error"));
      Toastify("error", action.payload);
    }
  }
});

export const {
  getProgressiveProfilingDataRequest,
  getProgressiveProfilingDataSuccess,
  getProgressiveProfilingDataFailure,
  updateProgressiveProfilingDataRequest,
  updateProgressiveProfilingDataSuccess,
  updateProgressiveProfilingDataFailure,
  deleteProgressiveProfilingDataRequest,
  deleteProgressiveProfilingDataSuccess,
  deleteProgressiveProfilingDataFailure
} = ProgressiveProfilingDataSlice.actions;
